export interface Client {
  id: number;
  name: string;
  email: string;
  plan: string;
  planPrice: number; // in euros
  status: 'active' | 'expiring' | 'expired';
  subscriptionStart: string;
  subscriptionEnd: string;
  progress: number; // 0-100
  lastActiveDate: Date;
  academyProgress: {
    academyId: string;
    academyName: string;
    completedModules: number;
    totalModules: number;
  }[];
}

// Mock data for clients
export const mockClients: Client[] = [
  { 
    id: 1, 
    name: 'John Doe', 
    email: 'john@example.com', 
    plan: 'Lifetime',
    planPrice: 37,
    status: 'active',
    subscriptionStart: '2024-01-15',
    subscriptionEnd: '2025-01-15',
    progress: 78,
    lastActiveDate: new Date(Date.now() - 2 * 60 * 60 * 1000), // 2 hours ago
    academyProgress: [
      { academyId: 'faceless-content', academyName: 'Faceless Content Systems', completedModules: 10, totalModules: 10 },
      { academyId: 'phantom-websites', academyName: 'Phantom Websites & Blogs', completedModules: 8, totalModules: 10 },
      { academyId: 'invisible-ecom', academyName: 'Invisible E-Com', completedModules: 6, totalModules: 10 },
      { academyId: 'ghost-newsletters', academyName: 'Ghost Newsletters', completedModules: 5, totalModules: 10 },
      { academyId: 'automated-business', academyName: 'Automation Empire', completedModules: 0, totalModules: 10 },
    ]
  },
  { 
    id: 2, 
    name: 'Sarah Wilson', 
    email: 'sarah@example.com', 
    plan: 'Monthly',
    planPrice: 4.99,
    status: 'active',
    subscriptionStart: '2024-11-20',
    subscriptionEnd: '2024-12-20',
    progress: 45,
    lastActiveDate: new Date(Date.now() - 24 * 60 * 60 * 1000), // 1 day ago
    academyProgress: [
      { academyId: 'faceless-content', academyName: 'Faceless Content Systems', completedModules: 10, totalModules: 10 },
      { academyId: 'phantom-websites', academyName: 'Phantom Websites & Blogs', completedModules: 7, totalModules: 10 },
      { academyId: 'invisible-ecom', academyName: 'Invisible E-Com', completedModules: 3, totalModules: 10 },
      { academyId: 'ghost-newsletters', academyName: 'Ghost Newsletters', completedModules: 0, totalModules: 10 },
      { academyId: 'automated-business', academyName: 'Automation Empire', completedModules: 0, totalModules: 10 },
    ]
  },
  { 
    id: 3, 
    name: 'Mike Johnson', 
    email: 'mike@example.com', 
    plan: 'Lifetime',
    planPrice: 37,
    status: 'active',
    subscriptionStart: '2024-03-10',
    subscriptionEnd: '2025-03-10',
    progress: 92,
    lastActiveDate: new Date(Date.now() - 5 * 60 * 1000), // 5 mins ago
    academyProgress: [
      { academyId: 'faceless-content', academyName: 'Faceless Content Systems', completedModules: 10, totalModules: 10 },
      { academyId: 'phantom-websites', academyName: 'Phantom Websites & Blogs', completedModules: 10, totalModules: 10 },
      { academyId: 'invisible-ecom', academyName: 'Invisible E-Com', completedModules: 10, totalModules: 10 },
      { academyId: 'ghost-newsletters', academyName: 'Ghost Newsletters', completedModules: 8, totalModules: 10 },
      { academyId: 'automated-business', academyName: 'Automation Empire', completedModules: 6, totalModules: 10 },
    ]
  },
  { 
    id: 4, 
    name: 'Emma Brown', 
    email: 'emma@example.com', 
    plan: 'Monthly',
    planPrice: 4.99,
    status: 'expiring',
    subscriptionStart: '2024-11-25',
    subscriptionEnd: '2024-12-25',
    progress: 34,
    lastActiveDate: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000), // 3 days ago
    academyProgress: [
      { academyId: 'faceless-content', academyName: 'Faceless Content Systems', completedModules: 10, totalModules: 10 },
      { academyId: 'phantom-websites', academyName: 'Phantom Websites & Blogs', completedModules: 5, totalModules: 10 },
      { academyId: 'invisible-ecom', academyName: 'Invisible E-Com', completedModules: 2, totalModules: 10 },
      { academyId: 'ghost-newsletters', academyName: 'Ghost Newsletters', completedModules: 0, totalModules: 10 },
      { academyId: 'automated-business', academyName: 'Automation Empire', completedModules: 0, totalModules: 10 },
    ]
  },
  { 
    id: 5, 
    name: 'Test User', 
    email: 'test@test.com', 
    plan: 'Lifetime',
    planPrice: 37,
    status: 'active',
    subscriptionStart: '2024-06-01',
    subscriptionEnd: '2025-06-01',
    progress: 67,
    lastActiveDate: new Date(), // Just now
    academyProgress: [
      { academyId: 'faceless-content', academyName: 'Faceless Content Systems', completedModules: 10, totalModules: 10 },
      { academyId: 'phantom-websites', academyName: 'Phantom Websites & Blogs', completedModules: 4, totalModules: 10 },
      { academyId: 'invisible-ecom', academyName: 'Invisible E-Com', completedModules: 2, totalModules: 10 },
      { academyId: 'ghost-newsletters', academyName: 'Ghost Newsletters', completedModules: 0, totalModules: 10 },
      { academyId: 'automated-business', academyName: 'Automation Empire', completedModules: 0, totalModules: 10 },
    ]
  },
  { 
    id: 6, 
    name: 'Alex Chen', 
    email: 'alex@example.com', 
    plan: 'Monthly',
    planPrice: 4.99,
    status: 'expired',
    subscriptionStart: '2024-11-01',
    subscriptionEnd: '2024-12-01',
    progress: 23,
    lastActiveDate: new Date(Date.now() - 14 * 24 * 60 * 60 * 1000), // 2 weeks ago
    academyProgress: [
      { academyId: 'faceless-content', academyName: 'Faceless Content Systems', completedModules: 7, totalModules: 10 },
      { academyId: 'phantom-websites', academyName: 'Phantom Websites & Blogs', completedModules: 3, totalModules: 10 },
      { academyId: 'invisible-ecom', academyName: 'Invisible E-Com', completedModules: 0, totalModules: 10 },
      { academyId: 'ghost-newsletters', academyName: 'Ghost Newsletters', completedModules: 0, totalModules: 10 },
      { academyId: 'automated-business', academyName: 'Automation Empire', completedModules: 0, totalModules: 10 },
    ]
  },
];

// Helper functions for calculations
export const getActiveClients = (clients: Client[]) => 
  clients.filter(c => c.status === 'active');

export const getExpiringClients = (clients: Client[]) => 
  clients.filter(c => c.status === 'expiring');

export const getExpiredClients = (clients: Client[]) => 
  clients.filter(c => c.status === 'expired');

export const getActiveRate = (clients: Client[]) => {
  if (clients.length === 0) return 0;
  return Math.round((getActiveClients(clients).length / clients.length) * 100);
};

export const getAverageProgress = (clients: Client[]) => {
  if (clients.length === 0) return 0;
  return Math.round(clients.reduce((acc, c) => acc + c.progress, 0) / clients.length);
};

export const getMonthlyRevenue = (clients: Client[]) => {
  const activeClients = getActiveClients(clients);
  return activeClients.reduce((acc, c) => acc + (c.planPrice / 12), 0);
};

export const getTotalRevenue = (clients: Client[]) => {
  const activeClients = getActiveClients(clients);
  return activeClients.reduce((acc, c) => acc + c.planPrice, 0);
};

export const formatLastActive = (date: Date): string => {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  const diffWeeks = Math.floor(diffDays / 7);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins} min${diffMins > 1 ? 's' : ''} ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
  if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  return `${diffWeeks} week${diffWeeks > 1 ? 's' : ''} ago`;
};

export const getDaysUntilExpiration = (endDate: string): number => {
  const end = new Date(endDate);
  const now = new Date();
  const diffMs = end.getTime() - now.getTime();
  return Math.ceil(diffMs / (1000 * 60 * 60 * 24));
};

export const calculateClientProgress = (client: Client): number => {
  const totalModules = client.academyProgress.reduce((acc, a) => acc + a.totalModules, 0);
  const completedModules = client.academyProgress.reduce((acc, a) => acc + a.completedModules, 0);
  if (totalModules === 0) return 0;
  return Math.round((completedModules / totalModules) * 100);
};

export const getClientById = (clients: Client[], id: number): Client | undefined => {
  return clients.find(c => c.id === id);
};
